import React, { useEffect, useState, useMemo } from "react";

// ──────────────────────────────────────────────────────
// AdminCourierFreight.jsx — 특송운임 요율 자체 관리 (TRADEVERSE 일회성 이관 후 loginHS 소유)
//   GET  /api/tools/courier-freight/admin/status          현황(헤더 이력·행수)
//   POST /api/tools/courier-freight/admin/rates           새 기준일 등록(CSV, preview 지원)
//   DEL  /api/tools/courier-freight/admin/rates/:seq      요율 세트 삭제
//   PUT  /api/tools/courier-freight/admin/nat             국가 zone·급증수수료 수정
//   admin_token Bearer — AdminApiUsage 패턴 미러.
// ──────────────────────────────────────────────────────

const CARRIERS = ["DHL", "FEDEX", "UPS", "UNIPASS"];
const fmtDt = (d) => (d && d.length === 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}` : d || "—");

const S = {
  card: { border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16, background: "#fff" },
  h: { fontSize: 15, fontWeight: 700, marginBottom: 10 },
  th: { textAlign: "left", padding: "6px 10px", borderBottom: "2px solid #e5e7eb", fontSize: 12.5, whiteSpace: "nowrap" },
  td: { padding: "6px 10px", borderBottom: "1px solid #f3f4f6", fontSize: 13, verticalAlign: "top" },
  input: { padding: "8px 10px", fontSize: 13.5, borderRadius: 7, border: "1px solid #d1d5db", fontFamily: "inherit", boxSizing: "border-box" },
  btn: { background: "#111827", color: "#fff", border: "none", borderRadius: 7, padding: "9px 16px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  btnGhost: { background: "#fff", color: "#111827", border: "1.5px solid #d1d5db", borderRadius: 7, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  ta: { width: "100%", minHeight: 120, padding: 10, fontSize: 12.5, fontFamily: "ui-monospace,monospace", borderRadius: 7, border: "1px solid #d1d5db", boxSizing: "border-box", whiteSpace: "pre" },
  msgOk: { marginTop: 8, fontSize: 13, color: "#137333" },
  msgErr: { marginTop: 8, fontSize: 13, color: "#b91c1c" },
};

function authHeaders(json) {
  const t = localStorage.getItem("admin_token");
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
}

export default function AdminCourierFreight({ API_BASE_URL }) {
  const base = `${API_BASE_URL || ""}/api/tools/courier-freight/admin`;
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState(null);

  // 새 요율 등록 폼
  const [type, setType] = useState("DHL");
  const [baseDt, setBaseDt] = useState("");
  const [fuelPer, setFuelPer] = useState("");
  const [freCsv, setFreCsv] = useState("");
  const [fuelCsv, setFuelCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [regMsg, setRegMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  // 국가 편집
  const [natType, setNatType] = useState("DHL");
  const [nats, setNats] = useState([]);
  const [natFilter, setNatFilter] = useState("");
  const [natEdits, setNatEdits] = useState({}); // seq → {area, surge_fee}
  const [natMsg, setNatMsg] = useState(null);

  const loadStatus = () => {
    fetch(`${base}/status`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
      .then(setStatus)
      .catch((e) => setErr(String(e.message || e)));
  };
  useEffect(loadStatus, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadNats = () => {
    fetch(`${base}/nat?type=${natType}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
      .then((d) => {
        setNats(d.rows || []);
        setNatEdits({});
      })
      .catch(() => setNats([]));
  };
  useEffect(loadNats, [natType]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (isPreview) => {
    setBusy(true);
    setRegMsg(null);
    setPreview(null);
    try {
      const r = await fetch(`${base}/rates`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({
          type, base_dt: baseDt.replace(/-/g, ""), fuel_per: fuelPer,
          fre_csv: freCsv, fuel_csv: fuelCsv, preview: isPreview,
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        setRegMsg({ ok: false, text:
          d.errorCode === "DUPLICATE_BASE_DT"
            ? `같은 기준일이 이미 있습니다 (seq ${d.seq}) — 아래 이력에서 삭제 후 재등록하세요.`
            : `등록 실패: ${d.errorCode || r.status} (CSV 형식·기준일 확인)` });
      } else if (isPreview) {
        setPreview(d);
      } else {
        setRegMsg({ ok: true, text:
          `등록 완료 — seq ${d.seq}, ${d.type} ${fmtDt(d.base_dt)}, zone ${d.zones.length}개 · ${d.inserted}행. 계산기는 즉시 이 요율을 사용합니다.` });
        setPreview(null);
        loadStatus();
      }
    } catch (e) {
      setRegMsg({ ok: false, text: "네트워크 오류" });
    } finally { setBusy(false); }
  };

  const del = async (seq, label) => {
    if (!window.confirm(`${label} 요율 세트(seq ${seq})를 삭제할까요?\n최신 세트를 지우면 이전 기준일로 롤백됩니다.`)) return;
    const r = await fetch(`${base}/rates/${seq}`, { method: "DELETE", headers: authHeaders() });
    if (r.ok) loadStatus();
    else window.alert("삭제 실패: HTTP " + r.status);
  };

  const saveNats = async () => {
    const updates = Object.entries(natEdits).map(([seq, v]) => ({ seq: Number(seq), ...v }));
    if (!updates.length) { setNatMsg({ ok: false, text: "변경된 행이 없습니다." }); return; }
    setNatMsg(null);
    const r = await fetch(`${base}/nat`, {
      method: "PUT", headers: authHeaders(true), body: JSON.stringify({ updates }),
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { setNatMsg({ ok: true, text: `저장 완료 — ${d.updated}행 반영.` }); loadNats(); }
    else setNatMsg({ ok: false, text: "저장 실패: " + (d.errorCode || r.status) });
  };

  const nf = natFilter.trim().toUpperCase();
  const natRows = useMemo(() => {
    const rows = nf
      ? nats.filter((n) => (n.nat_nm || "").toUpperCase().includes(nf) || (n.nat || "").includes(nf))
      : nats;
    return rows.slice(0, 80);
  }, [nats, nf]);

  return (
    <div>
      {err && <div style={S.msgErr}>현황 조회 실패: {err} (관리자 로그인 확인)</div>}

      {/* 현황 */}
      <div style={S.card}>
        <div style={S.h}>요율 현황 (캐리어별 이력 — 최신 seq 가 계산기에 적용)</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={S.th}>캐리어</th><th style={S.th}>기준일</th><th style={S.th}>유류할증비율</th>
            <th style={S.th}>요율 행수</th><th style={S.th}>seq</th><th style={S.th}></th>
          </tr></thead>
          <tbody>
            {(status?.headers || []).map((h, i, arr) => {
              const latest = !arr.slice(0, i).some((x) => x.type === h.type);
              return (
                <tr key={h.seq} style={latest ? { background: "#f0fdf4" } : undefined}>
                  <td style={S.td}>{h.type}{latest && <b style={{ color: "#137333" }}> ●적용중</b>}</td>
                  <td style={S.td}>{fmtDt(h.base_dt)}</td>
                  <td style={S.td}>{h.fuel_per != null ? `${h.fuel_per}%` : "—"}</td>
                  <td style={S.td}>{Number(h.rate_rows).toLocaleString()}</td>
                  <td style={S.td}>{h.seq}</td>
                  <td style={S.td}>
                    <button style={{ ...S.btnGhost, padding: "3px 10px", fontSize: 12, color: "#b91c1c", borderColor: "#fca5a5" }}
                      onClick={() => del(h.seq, `${h.type} ${fmtDt(h.base_dt)}`)}>삭제</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 새 요율 등록 */}
      <div style={S.card}>
        <div style={S.h}>새 요율 등록 (특송사 요율표 엑셀에서 복사 → 붙여넣기)</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <select value={type} onChange={(e) => setType(e.target.value)} style={S.input}>
            {CARRIERS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input type="date" value={baseDt} onChange={(e) => setBaseDt(e.target.value)} style={S.input} title="기준일" />
          <input type="number" placeholder="유류할증비율 % (표시용, 선택)" value={fuelPer}
            onChange={(e) => setFuelPer(e.target.value)} style={{ ...S.input, width: 210 }} />
        </div>
        <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 4 }}>
          운임표 (필수) — 1행: <code>중량 ZONE1 ZONE2 …</code>, 이후 행: 중량과 zone 별 금액.
          엑셀 범위를 그대로 붙여넣으면 됩니다(탭 구분·천단위 콤마 허용).
        </div>
        <textarea style={S.ta} value={freCsv} onChange={(e) => setFreCsv(e.target.value)}
          placeholder={"중량\tZONE1\tZONE2\n0.5\t75,000\t78,500\n1.0\t85,450\t89,300"} />
        <div style={{ fontSize: 12.5, color: "#6b7280", margin: "10px 0 4px" }}>
          유류할증료표 (선택) — 같은 형식. 비우면 유류할증 0 으로 계산됩니다.
        </div>
        <textarea style={{ ...S.ta, minHeight: 80 }} value={fuelCsv} onChange={(e) => setFuelCsv(e.target.value)} />
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button style={S.btnGhost} disabled={busy} onClick={() => submit(true)}>미리보기(검증)</button>
          <button style={S.btn} disabled={busy || !preview} onClick={() => submit(false)}
            title={preview ? "" : "먼저 미리보기로 검증하세요"}>
            {busy ? "처리 중…" : "등록"}
          </button>
        </div>
        {preview && (
          <div style={S.msgOk}>
            ✔ 파싱 OK — {preview.type} {fmtDt(preview.base_dt)} · zone {preview.zones.join(", ")} ·
            중량 {preview.row_count}행 · 유류할증 셀 {preview.fuel_cells}개.
            첫 행 예: {JSON.stringify(preview.sample?.[0]?.vals)}. 이상 없으면 [등록]을 누르세요.
          </div>
        )}
        {regMsg && <div style={regMsg.ok ? S.msgOk : S.msgErr}>{regMsg.text}</div>}
      </div>

      {/* 국가 zone·급증수수료 편집 */}
      <div style={S.card}>
        <div style={S.h}>국가 ZONE·급증수수료 편집</div>
        <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
          <select value={natType} onChange={(e) => setNatType(e.target.value)} style={S.input}>
            {CARRIERS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input placeholder="국가 검색 (이름/코드)" value={natFilter}
            onChange={(e) => setNatFilter(e.target.value)} style={{ ...S.input, width: 220 }} />
          <button style={S.btn} onClick={saveNats}>변경 저장</button>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={S.th}>코드</th><th style={S.th}>국가명</th>
            <th style={S.th}>ZONE</th><th style={S.th}>급증수수료(원)</th>
          </tr></thead>
          <tbody>
            {natRows.map((n) => {
              const e = natEdits[n.seq] || {};
              return (
                <tr key={n.seq ?? `${n.nat}-${n.area}`}>
                  <td style={S.td}>{n.nat}</td>
                  <td style={S.td}>{n.nat_nm}</td>
                  <td style={S.td}>
                    <input style={{ ...S.input, width: 90, padding: "4px 8px" }}
                      value={e.area !== undefined ? e.area : n.area || ""}
                      onChange={(ev) => setNatEdits((p) => ({
                        ...p, [n.seq]: { area: ev.target.value,
                          surge_fee: e.surge_fee !== undefined ? e.surge_fee : n.surge_fee } }))} />
                  </td>
                  <td style={S.td}>
                    <input style={{ ...S.input, width: 110, padding: "4px 8px" }} type="number"
                      value={e.surge_fee !== undefined ? e.surge_fee : (n.surge_fee ?? "")}
                      onChange={(ev) => setNatEdits((p) => ({
                        ...p, [n.seq]: { area: e.area !== undefined ? e.area : n.area,
                          surge_fee: ev.target.value } }))} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ marginTop: 6, fontSize: 12, color: "#9ca3af" }}>
          최대 80행 표시 — 검색으로 좁혀서 편집하세요. 변경 행만 저장됩니다.
        </div>
        {natMsg && <div style={natMsg.ok ? S.msgOk : S.msgErr}>{natMsg.text}</div>}
      </div>
    </div>
  );
}
